import { makeAutoObservable } from "mobx";
import { logDebug, logError } from "../LocalLogger";
import { RestAPIProvider } from "./RestAPI";
import { localLargeStorage } from "./LocalLargeStorage";
import { ProductType } from "../Pages/productTypes";

export interface BlueprintSimple {
	id: number;
	title: string;
	brand: string;
	images: string[];
	description?: string;
	model?: string;
}

export interface PrintProvider {
	blueprints?: BlueprintSimple[];
	id: number;
	title: string;
	location: {
		address1: string;
		city: string;
		country: string;
		region: string;
		zip: string;
	};
}
export interface PrintifyImage {
	id: string;
	file_name: string;
	height: number;
	width: number;
	mime_type: string;
	preview_url: string;
	size: number;
	upload_time: string;
}
export const noop = () => {};

export async function webAPIRequest(api: string, args: any) {
	try {
		const ret = await RestAPIProvider.fetchAPI(
			"test/webAPI",
			"POST",
			{
				api,
				args,
			},
			"data",
			true
		);
		return ret;
	} catch (err) {}
}
export async function printifyRequest(api: string, args: any) {
	try {
		const ret = await RestAPIProvider.fetchAPI(
			"test/printifyAPI",
			"POST",
			{
				api,
				args,
			},
			"data",
			true
		);
		return ret;
	} catch (err) {}
}
class AppMobx {
	// Private variables
	private _allBlueprints: BlueprintSimple[] = [];
	private _allPrintProviders: PrintProvider[] = [];
	private _allUKBlueprints: BlueprintSimple[] = [];
	private _allImages: PrintifyImage[] = [];
	private _selectedProviderId: number = 72;
	private _selectedUKProviderBlueprints: BlueprintSimple[] = [];
	private _showLoading: number = 0;
	private _selectedDesignId: string | null = null;
	private _products: any = {};
	public getProduct(p: string) {
		return this._products[p] || null;
	}
	public setProduct(p: string, v: ProductType) {
		this._products[p] = v;
	}
	public get selectedDesign() {
		return this._selectedDesignId;
	}
	public set selectedDesign(value: string | null) {
		this._selectedDesignId = value;
	}
	public get showLoading() {
		return this._showLoading > 0;
	}
	public set showLoading(value: boolean) {
		this._showLoading = value ? this._showLoading + 1 : Math.max(0, this._showLoading - 1);
	}

	public get allImages() {
		return this._allImages;
	}

	private _onlyUKProviders: PrintProvider[] = [];
	public get onlyUKProviders() {
		return this._onlyUKProviders;
	}

	public get allBlueprints() {
		return this._allBlueprints;
	}
	constructor() {
		makeAutoObservable(this);
	}
	async getLocal<T>(key: string, params: any): Promise<T | null> {
		const fullKey = `${key}-${JSON.stringify(params)}`;
		try {
			const item = await localLargeStorage.getItem(fullKey);
			if (item) {
				return JSON.parse(item) as T;
			}
		} catch (err) {}
		return null;
	}
	async getRemote<T>(key: string, params: any): Promise<T | null> {
		const fullKey = `${key}-${JSON.stringify(params)}`;
		try {
			const item = await printifyRequest(key, params);
			if (item) {
				await localLargeStorage.setItem(fullKey, JSON.stringify(item));
				return item as T;
			}
		} catch (err) {
			logError(`Failed to fetch remote data for key ${key}:`, err);
		}
		return null;
	}
	async get<T>(key: string, params: any = {}): Promise<T | null> {
		try {
			const localData = await this.getLocal<T>(key, params);
			if (localData) {
				// logDebug(`Found local data for key ${key}:`, localData);
				return localData;
			}
			const remoteData = await this.getRemote<T>(key, params);
			if (remoteData) {
				// logDebug(`Fetched remote data for key ${key}:`, remoteData);
				return remoteData;
			}
		} catch (err) {
			logError(`Error getting data for key ${key}:`, err);
		}
		return null;
	}

	public get selectedProvider() {
		return this._allPrintProviders.find((p) => p.id === this._selectedProviderId);
	}
	public get selectedProviderId() {
		return this._selectedProviderId;
	}
	public get selectedUKProviderBlueprints() {
		return this._getProviderBlueprints(this._selectedProviderId);
	}
	private _getProviderBlueprints(providerId: number): BlueprintSimple[] {
		const provider = this._allPrintProviders.find((p) => p.id === providerId);
		if (!provider) {
			logError(`Provider with ID ${providerId} not found`);
			return [];
		}
		return provider.blueprints || [];
	}
	async init() {
		// const image = await this.uploadImage("Schiehallion.png");
		this._allBlueprints = (await this.get<BlueprintSimple[]>("allBlueprints")) || [];
		this._allPrintProviders = (await this.get<PrintProvider[]>("getAllPrintProviders")) || [];
		this._onlyUKProviders = this._allPrintProviders.filter((p) => p.location.country === "GB");

		this._onlyUKProviders = this._allPrintProviders.filter((p) => p.location.country === "GB");
		for (const provider of this._onlyUKProviders) {
			const providerData = await this.get<PrintProvider>("getProviderCatalog", { providerId: provider.id });
			if (!providerData) {
				logError(`Failed to fetch provider data for provider ID ${provider.id}`);
				continue;
			}
			// const providerData = (await printifyRequest("getProviderCatalog", { providerId: provider.id })) as PrintProvider;
			provider.blueprints = providerData.blueprints || [];
			this._allUKBlueprints.push(
				...(providerData.blueprints?.filter(
					(blueprint) => !this._allUKBlueprints.some((b: BlueprintSimple) => b.id === blueprint.id)
				) || [])
			);
		}
		this._onlyUKProviders = this._onlyUKProviders.filter((p) => p.blueprints?.length);
		// logDebug("Filtered UK print providers:", this._onlyUKProviders);
		// logDebug("All UK blueprints:", this._allUKBlueprints);

		this._selectedProviderId = 72;
		this._selectedUKProviderBlueprints = this._getProviderBlueprints(this._selectedProviderId);

		const testBlueprint = await this.get<BlueprintSimple>("getBlueprint", {
			blueprintId: this._selectedUKProviderBlueprints[0].id,
		});
		if (testBlueprint) {
			// logDebug("Test blueprint:", testBlueprint);
			const testVariants = await this.get("getVariants", {
				blueprintId: testBlueprint.id,
				providerId: this._selectedProviderId,
			});
			// const testVariants = await printifyRequest("getVariants", {
			// 	blueprintId: testBlueprint.id,
			// 	providerId: this._onlyUKProviders[0].id,
			// });
			if (!testVariants) {
				logError(`Failed to fetch variants for blueprint ID ${testBlueprint.id}`);
				return;
			}
			// logDebug("Test variants:", { ...testVariants, blueprintId: testBlueprint.id });
		}
		appStore.refreshImages();

		// const testBlueprint = (await printifyRequest("getBlueprint", {
		// 	blueprintId: this._onlyUKProviders[0].blueprints![0].id,
		// })) as BlueprintSimple;
		// const targets = [
		// 	{ name: "T‑Shirt", key: "T-Shirt" },
		// 	// { name: "Sticker", key: "Sticker" },
		// 	// { name: "Wall Clock", key: "Clock" },
		// ];

		// for (const t of targets) {
		// 	const bp = await printifyService.findBlueprint(t.key);
		// 	// logDebug(`Found blueprint for ${t.name}:`, bp);
		// 	// if (!bp) {
		// 	// 	logError(`${t.name} blueprint not found`);
		// 	// 	continue;
		// 	// }
		// 	// const { provider, variants } = await this.findProviderAndVariants(bp);
		// 	// await this.makeProduct(t.name, image, bp, provider, variants);
		// }
	}

	async refreshImages() {
		// logDebug("Refreshing images...");
		const allImages = await printifyRequest("getAllImages", {});
		this._allImages = allImages.data;
		// logDebug("Images refreshed:", this._allImages);
	}
}

export const appStore = new AppMobx();
